import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import {
  ArrowForward,
  CheckCircle,
  FileDownload,
  Print,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../../components/lab-dynamic-table";
import DateRangeSelector from "../DateRangeSelector";
import { formatCurrency } from "../../utils/numberUtils";

// Interface for the detailed share data
interface DoctorShareDetail {
  _id: string;
  doctorId: string;
  name: string;
  amount: number;
  discount: number;
  netAmount: number;
  paidAmount?: number;
  patientInfo: {
    patientName: string;
    mrn: number;
    guardiansName: string;
    phonNumber: string;
    gender: string;
  };
  createdAt: string;
  createdBy: string;
  procedure: string;
  department: string;
  isProcessed: boolean;
}

export const ShareRevenueTab: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const [revenueData, setRevenueData] = useState<DoctorShareDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [doctorId, setDoctorId] = useState<string>("");
  const [selectedProcessedStatus, setSelectedProcessedStatus] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemsToProcess, setItemsToProcess] = useState<DoctorShareDetail[]>([]);
  const [paymentMethod, setPaymentMethod] = useState("Cash"); // Default payment method
  const [isProceeding, setIsProceeding] = useState(false);

  // Handle select all checkbox
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = new Set(revenueData.map(item => item._id));
      setSelectedRows(allIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  // Handle individual row checkbox
  const handleSelectRow = (id: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Check if all rows are selected
  const isAllSelected =
    revenueData.length > 0 && selectedRows.size === revenueData.length;
  const isIndeterminate =
    selectedRows.size > 0 && selectedRows.size < revenueData.length;

  const handleProceed = (singleItemId?: string) => {
    let items: Set<string>;

    if (singleItemId) {
      items = new Set([singleItemId]);
    } else {
      if (selectedRows.size === 0) {
        toast.warn("Please select at least one record to proceed.");
        return;
      }
      items = selectedRows;
    }

    const selectedData = revenueData.filter(item => items.has(item._id));
    setItemsToProcess(selectedData); // Store data for the modal
    setIsModalOpen(true); // Open the modal
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setItemsToProcess([]); // Clear data when modal closes
  };

  const handleConfirmProceed = async () => {
    // 1. Prepare the data for the API
    const payload = {
      drShareIds: itemsToProcess.map(item => item._id),
      paymentMode:
        paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1), // Capitalize first letter e.g., "Cash"
    };

    // 2. Set loading state to true
    setIsProceeding(true);

    try {
      // 3. Make the API call
      const response = await axios.put(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/proceedDrShare`,
        payload, // The request body
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`, // Don't forget the token
          },
        }
      );

      if (response.data.isSuccess) {
        toast.success(
          response.data.message || "Successfully processed records!"
        );
        // 4. On success, close the modal and refresh the table data
        handleCloseModal();
        setSelectedRows(new Set()); // Clear selection in the table
        fetchShareRevenue(); // Refresh the table to show updated data
      } else {
        toast.error(response.data.message || "Failed to process records.");
      }
    } catch (err: any) {
      console.error("Error proceeding with Dr. Share:", err);
      toast.error(
        err.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      // 5. Set loading state back to false, regardless of success or failure
      setIsProceeding(false);
    }
  };
  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
  };

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getAllSharerDoctors`,
        {
          params: { pageNo: 1, count: 500 },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDoctors(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchShareRevenue = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/viewDrShareDetails`,
        {
          params: { 
            fromDate, 
            toDate, 
            doctorId,
            ...(selectedProcessedStatus && { isProcessed: selectedProcessedStatus === "true" })
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setRevenueData(response.data.data || []);
        setSelectedRows(new Set()); // Clear selection when new data loads
      } else {
        toast.error(response.data.message || "Failed to fetch share revenue");
      }
    } catch (err: any) {
      toast.error(err.message || "Error fetching share revenue data");
      setRevenueData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShareRevenue();
  }, [fromDate, toDate, doctorId, selectedProcessedStatus]);

  const handleExcelExport = () => {
    if (!revenueData || revenueData.length === 0) {
      toast.warn("No data to export.");
      return;
    }
    const worksheetData = revenueData.map(row => ({
      MRN: row.patientInfo?.mrn || "N/A",
      "PATIENT NAME": row.patientInfo?.patientName || "N/A",
      "GUARDIAN NAME": row.patientInfo?.guardiansName || "N/A",
      "DOCTOR NAME": row.name || "N/A",
      PROCEDURE: row.procedure || "N/A",
      DEPARTMENT: row.department || "N/A",
      AMOUNT: row.amount?.toFixed(2) || "0.00",
      DISCOUNT: row.discount?.toFixed(2) || "0.00",
      "NET AMOUNT": row.netAmount?.toFixed(2) || "0.00",
      "PAID AMOUNT": (row.paidAmount || 0).toFixed(2),
      "CREATED BY": row.createdBy || "N/A",
      "CREATED AT": formatDateTime(row.createdAt) || "N/A",
    }));

    // Calculate totals
    const totalAmount = revenueData.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);
    const totalDiscount = revenueData.reduce((sum, item) => sum + (Number(item?.discount) || 0), 0);
    const totalNetAmount = revenueData.reduce((sum, item) => sum + (Number(item?.netAmount) || 0), 0);
    const totalPaidAmount = revenueData.reduce((sum, item) => sum + (Number(item?.paidAmount) || 0), 0);

    // Add total row
    worksheetData.push({
      MRN: "",
      "PATIENT NAME": "",
      "GUARDIAN NAME": "",
      "DOCTOR NAME": "",
      PROCEDURE: "",
      DEPARTMENT: "TOTAL",
      AMOUNT: Number(totalAmount).toFixed(2),
      DISCOUNT: Number(totalDiscount).toFixed(2),
      "NET AMOUNT": Number(totalNetAmount).toFixed(2),
      "PAID AMOUNT": Number(totalPaidAmount).toFixed(2),
      "CREATED BY": "",
      "CREATED AT": "",
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "DoctorShareRevenue");
    xlsx.writeFile(
      workbook,
      `Doctor_Share_Revenue_${fromDate}_to_${toDate}.xlsx`
    );
  };

  const handlePrint = () => {
    if (!revenueData || revenueData.length === 0) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Professional header with minimal, clean design
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("DOCTOR SHARE REVENUE REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });
    
    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Subtle metadata section with smaller font
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "MRN",
        "PATIENT NAME",
        "GUARDIAN NAME",
        "DOCTOR NAME",
        "PROCEDURE",
        "DEPARTMENT",
        "AMOUNT",
        "DISCOUNT",
        "NET AMOUNT",
        "PAID AMOUNT",
        "CREATED BY",
        "CREATED AT",
      ],
    ];

    const body = revenueData.map(row => [
      row.patientInfo?.mrn || "N/A",
      row.patientInfo?.patientName || "N/A",
      row.patientInfo?.guardiansName || "N/A",
      row.name || "N/A",
      row.procedure || "N/A",
      row.department || "N/A",
      formatCurrency(row.amount || 0),
      formatCurrency(row.discount || 0),
      formatCurrency(row.netAmount || 0),
      formatCurrency(row.paidAmount || 0),
      row.createdBy || "N/A",
      formatDateTime(row.createdAt) || "N/A",
    ]);

    // Calculate totals
    const totalAmount = revenueData.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);
    const totalDiscount = revenueData.reduce((sum, item) => sum + (Number(item?.discount) || 0), 0);
    const totalNetAmount = revenueData.reduce((sum, item) => sum + (Number(item?.netAmount) || 0), 0);
    const totalPaidAmount = revenueData.reduce((sum, item) => sum + (Number(item?.paidAmount) || 0), 0);

    body.push([
      "",
      "",
      "",
      "",
      "",
      "TOTAL",
      formatCurrency(totalAmount),
      formatCurrency(totalDiscount),
      formatCurrency(totalNetAmount),
      formatCurrency(totalPaidAmount),
      "",
      "",
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: { 
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8
      },
      columnStyles: {
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function(data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 5.8;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = { top: 2.5, right: 2, bottom: 2.5, left: 2 };
        }
      },
      didDrawPage: function(data) {
        // Add subtle footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;
        
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  // Headers array - DynamicTable accepts (string | React.ReactNode)[]
  const tableHeaders: any[] = [
    <Checkbox
      key="select-all"
      checked={isAllSelected}
      indeterminate={isIndeterminate}
      onChange={handleSelectAll}
      sx={{
        color: "#fff",
        padding: 0,
        "&.Mui-checked": {
          color: "#fff",
        },
        "&.MuiCheckbox-indeterminate": {
          color: "#fff",
        },
      }}
    />,
    "MRN",
    "PATIENT NAME",
    "GUARDIAN NAME",
    "DOCTOR NAME",
    "PROCEDURE",
    "DEPARTMENT",
    "AMOUNT",
    "DISCOUNT",
    "NET AMOUNT",
    "PAID AMOUNT",
    "CREATED BY",
    "CREATED AT",
    "ACTION",
  ];

  // Reusable button styles
  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  // Calculate totals for Amount, Discount, Net Amount, and Paid Amount
  const totalAmount = revenueData.reduce((sum, item) => sum + item.amount, 0);
  const totalDiscount = revenueData.reduce(
    (sum, item) => sum + item.discount,
    0
  );
  const totalNetAmount = revenueData.reduce(
    (sum, item) => sum + item.netAmount,
    0
  );
  const totalPaidAmount = revenueData.reduce(
    (sum, item) => sum + (item.paidAmount || 0),
    0
  );

  // Add a total row to the table data
  const tableData = [
    ...revenueData.map(item => ({
      checkbox: (
        <Checkbox
          checked={selectedRows.has(item._id)}
          onChange={() => handleSelectRow(item._id)}
          sx={{
            color: "#1976d2",
            padding: 0,
            "&.Mui-checked": {
              color: "#1976d2",
            },
          }}
        />
      ),
      mrn: item.patientInfo?.mrn || "N/A",
      patientName: item.patientInfo?.patientName || "N/A",
      guardianName: item.patientInfo?.guardiansName || "N/A",
      doctorName: item.name || "N/A",
      procedure: item.procedure || "N/A",
      department: item.department || "N/A",
      amount: formatCurrency(item.amount),
      discount: formatCurrency(item.discount),
      netAmount: formatCurrency(item.netAmount),
      paidAmount: formatCurrency(item.paidAmount || 0),
      createdBy: item.createdBy || "N/A",
      createdAt: formatDateTime(item.createdAt) || "N/A",
      action: (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleProceed(item._id)}
          sx={{
            ...gradientButtonStyle,
            background: item?.isProcessed
              ? "linear-gradient(45deg, #4caf50, #81c784)"
              : "linear-gradient(45deg, #1976d2, #42a5f5)",
            cursor: item?.isProcessed ? "not-allowed" : "pointer",
            opacity: item?.isProcessed ? 0.8 : 1,
          }}
          endIcon={
            item?.isProcessed ? (
              <CheckCircle sx={{ color: "#fff" }} />
            ) : (
              <ArrowForward sx={{ color: "#fff" }} />
            )
          }
          disabled={item?.isProcessed}
        >
          {item?.isProcessed ? "Processed" : "Proceed"}
        </Button>
      ),
    })),
    {
      checkbox: null,
      mrn: "",
      patientName: "",
      guardianName: "",
      doctorName: "",
      procedure: "",
      department: <strong>Total</strong>,
      amount: <strong>{formatCurrency(totalAmount)}</strong>,
      discount: <strong>{formatCurrency(totalDiscount)}</strong>,
      netAmount: <strong>{formatCurrency(totalNetAmount)}</strong>,
      paidAmount: <strong>{formatCurrency(totalPaidAmount)}</strong>,
      createdBy: "",
      createdAt: "",
      action: null,
    },
  ];

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      {/* Filter and Action Buttons Section */}
      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Export Buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExcelExport}
            sx={gradientButtonStyle}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={gradientButtonStyle}
          >
            Print
          </Button>
          {selectedRows.size > 0 && (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleProceed()}
              sx={gradientButtonStyle}
            >
              Proceed ({selectedRows.size})
            </Button>
          )}
        </Box>

        {/* Date Filters */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
             Processe Status
            </Typography>
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: "150px" },
                background: "#F5F5FB",
                borderRadius: "12px",
                "& fieldset": { border: "none" },
              }}
            >
              <Select
                value={selectedProcessedStatus}
                className="custom-input"
                onChange={(e) => setSelectedProcessedStatus(e.target.value)}
                displayEmpty
               
           
              >
                <MenuItem value="" sx={{ fontSize: "12px" }}>
                  All
                </MenuItem>
                <MenuItem value="true" sx={{ fontSize: "12px" }}>
                  Processed
                </MenuItem>
                <MenuItem value="false" sx={{ fontSize: "12px" }}>
                  Unprocessed
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Select Doctor
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              options={doctors}
              getOptionLabel={option => option.fullName}
              value={doctors.find(doc => doc._id === doctorId) || null}
              onChange={(event, newValue) => {
                setDoctorId(newValue ? newValue._id : "");
              }}
              sx={{ minWidth: { xs: "100%", sm: "200px" } }}
              renderInput={params => (
                <TextField {...params} className="custom-input" />
              )}
            />
          </Box>

          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      {/* Dynamic Table Section */}
      <Box
        sx={{
          p: 1,
          maxHeight: "70vh",
          overflowY: "hidden",
        }}
      >
        <DynamicTable
          headers={tableHeaders}
          data={tableData}
          // totalAmount={revenueData.reduce(
          //   (acc, item) => acc + item.netAmount,
          //   0
          // )}
          height={"60vh"}
          loading={loading}
        />
      </Box>

      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="md">
        <DialogTitle>Confirm Transaction</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are about to proceed with the following record(s):
          </Typography>

          <Box sx={{ mt: 2, border: "1px solid #ddd", borderRadius: "4px" }}>
            {/* --- NEW: Header Row --- */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr 2fr 1fr",
                gap: 2,
                p: 1,
                borderBottom: "2px solid #ddd", // Make header border bolder
                backgroundColor: "#f7f7f7", // Slight background color for header
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                MRN
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Patient Name
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Doctor
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", textAlign: "right" }}
              >
                Amount
              </Typography>
            </Box>

            {/* --- Existing Data Rows --- */}
            {itemsToProcess?.map((item, index) => (
              <Box
                key={item._id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 2fr 2fr 1fr",
                  gap: 2,
                  p: 1,
                  borderBottom:
                    index < itemsToProcess?.length - 1
                      ? "1px solid #eee"
                      : "none",
                }}
              >
                <Typography variant="body2">
                  {item?.patientInfo?.mrn}
                </Typography>
                <Typography variant="body2">
                  {item?.patientInfo?.patientName}
                </Typography>
                <Typography variant="body2">{item?.name}</Typography>
                <Typography variant="body2" sx={{ textAlign: "right" }}>
                  {formatCurrency(item?.paidAmount && item?.paidAmount > 0 ? item?.paidAmount : item?.netAmount)}
                </Typography>
              </Box>
            ))}

            {/* Total Amount */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                p: 1,
                borderTop: "1px solid #ddd",
                mt: 1,
              }}
            >
              <Typography variant="h6">
                Total:{" "}
                {formatCurrency(
                  itemsToProcess.reduce((acc, item) => acc + (item?.paidAmount && item?.paidAmount > 0 ? item?.paidAmount : item?.netAmount), 0)
                )}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <FormControl size="small">
              <InputLabel>Method</InputLabel>
              <Select
                value={paymentMethod}
                label="Method"
                onChange={e => setPaymentMethod(e.target.value)}
              >
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Online Transaction">
                  Online Transaction
                </MenuItem>
              </Select>
            </FormControl>
            <Button
              onClick={handleConfirmProceed}
              variant="contained"
              color="primary"
              disabled={isProceeding}
            >
              {isProceeding ? "Processing..." : "Confirm Proceed"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
